import '@mantine/core/styles/FloatingIndicator.css';
import '@mantine/core/styles/SegmentedControl.css';

import { SegmentedControl } from '@mantine/core';
import clsx from 'clsx';
import endOfDay from 'date-fns/endOfDay';
import { useCallback, useMemo, useState } from 'react';
import { DateRangePickerProps } from 'react-date-range';
import {
  isUnaryRangeFilterOp,
  StackedFilter,
  useMinFilterValue,
} from 'src/components/Filters';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { Button, Popover } from 'src/core/ui';
import { CrossIcon, ExpandIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { DATE_RANGE_FILTER_OP_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  DateRangePresetName,
  formatDate,
  getPresetFromUiDateTimeRange,
  getRelativePresetFromUiDateTimeRange,
  tryGetDateRangePresetSuggestionText,
  tryGetDateRangeRelativePresetSuggestionText,
} from 'src/utils/dateTimeUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import {
  DateTimeRange,
  DateTimeRangeWithRelative,
  FilterOperationType,
} from 'src/WebApiController';

import { DatePickerInput } from '../DateSelector';
import { PosSelect } from '../PosSelect';
import { DateRange, DateRangePresetsProp } from './DateRangePresets';
import * as styles from './DateRangeSelector.css';
import { DateRangeSelectorValueChange } from './DateRangeSelector.types';
import * as dateRangeStyles from './DateRangeSelectorV1.css';
import { dateSelectors } from './DateSelectors';

// Types
type DateRangeSelectorType = 'Exact' | 'Relative' | 'Custom';

type DateRangePickerBaseProps = Omit<
  DateRangePickerProps,
  'range' | 'onChange'
>;

export type DateRangeSelectorProps = Omit<
  DateRangePresetsProp,
  'onClick' | 'selectedDateRange'
> &
  DateRangePickerBaseProps & {
    disableCustomDates?: boolean;
    defaultValue?: DateTimeRangeWithRelative | DateTimeRange | null;
    /**
     * The currently selected operation.
     * This is passed back in the onChange callback.
     */
    operation?: FilterOperationType | null;
    value?: DateTimeRangeWithRelative | DateTimeRange | null;
    onChange?: (change: DateRangeSelectorValueChange) => void;
    onBlur?: (change: DateRangeSelectorValueChange) => void;
    useSiteTimeZone?: boolean;
    displayTextStyle?: React.CSSProperties;
    disabled?: boolean;
    isEndDateFixed?: boolean;
    exactPresetNames?: DateRangePresetName[];
    trailingPresetNames?: DateRangePresetName[];
    /**
     * @deprecated - use exactPresetNames and trailingPresetNames instead
     */
    useRelativePresets?: boolean;
    /**
     * Whether to enable the operation select.
     * If true, the operation select will be shown.
     * If false, the operation will be assumed to be between or greater than or less than
     */
    enableOperationSelect?: boolean;
    /**
     * Whether to layout the options horizontally or vertically - this affects the layout
     * when operationOptionsContent is provided.
     * Defaults to vertical.
     */
    isHorizontalLayout?: boolean;
  };

// Helper functions
const getInitialSelectorType = (
  value: DateTimeRangeWithRelative | DateTimeRange | null | undefined,
  useSiteTimeZone: boolean,
  exactPresetNames?: DateRangePresetName[],
  trailingPresetNames?: DateRangePresetName[],
  toZonedTime?: (date: Date) => Date
): DateRangeSelectorType => {
  if (!value) {
    if (!exactPresetNames && trailingPresetNames) {
      return 'Relative';
    }
    if (exactPresetNames && !trailingPresetNames) {
      return 'Exact';
    }
    return 'Custom';
  }
  const relativeValue = value as DateTimeRangeWithRelative;
  const relativePresetMatch =
    getRelativePresetFromUiDateTimeRange(relativeValue);
  if (relativePresetMatch) {
    return 'Relative';
  }

  const presetMatch = getPresetFromUiDateTimeRange(
    value,
    useSiteTimeZone ? toZonedTime : undefined
  );

  if (presetMatch) {
    return 'Exact';
  }

  return 'Custom';
};

export const DateRangeSelector = ({
  className,
  defaultValue,
  dateDisplayFormat = 'MM/dd/yyyy',
  disableCustomDates = false,
  operation,
  value,
  useSiteTimeZone = false,
  startDatePlaceholder,
  endDatePlaceholder,
  retainEndDateOnFirstSelection = true,
  rangeColors = [vars.color.backgroundHighlight],
  locale = getLocaleFromLanguageOrCurrent(),
  displayTextStyle,
  disabled,
  onChange,
  onBlur,
  onPreviewChange,
  isEndDateFixed = false,
  exactPresetNames,
  trailingPresetNames,
  useRelativePresets,
  enableOperationSelect = false,
  isHorizontalLayout = false,
  ...presetProps
}: DateRangeSelectorProps) => {
  if (exactPresetNames === undefined && trailingPresetNames === undefined) {
    if (useRelativePresets) {
      trailingPresetNames = presetProps.presetNames;
      exactPresetNames = undefined;
    } else {
      exactPresetNames = presetProps.presetNames;
      trailingPresetNames = undefined;
    }
  }

  const { toZonedTime, fromZonedTime } = useSiteTimezoneContext();
  const currentLocale = getLocaleFromLanguageOrCurrent();

  // State hooks grouped together
  const [isOpen, setIsOpen] = useState(false);
  const [dateValue, setDateValue] = useState({
    start: formatDate(value?.start || '', currentLocale.code),
    end: formatDate(value?.end || '', currentLocale.code),
  });
  const [dateRange, setDateRange] = useState<DateTimeRangeWithRelative | null>(
    value as DateTimeRangeWithRelative
  );
  const [dateSelectorType, setDateSelectorType] =
    useState<DateRangeSelectorType>(() =>
      getInitialSelectorType(
        value,
        useSiteTimeZone,
        exactPresetNames,
        trailingPresetNames,
        toZonedTime
      )
    );

  // Build segmented control data based on enabled options
  const segmentedControlData = useMemo(() => {
    const data: DateRangeSelectorType[] = [];
    if (exactPresetNames) data.push('Exact');
    if (trailingPresetNames) data.push('Relative');
    if (!disableCustomDates) data.push('Custom');
    return data;
  }, [exactPresetNames, trailingPresetNames, disableCustomDates]);

  // Content hooks
  const defaultDateRangeDisplayText = useContent(
    ContentId.DateRangeDisplayText
  );
  const defaultFromlabel = useContent(ContentId.FromDate);
  const defaultToLabel = useContent(ContentId.ToDate);

  const highlightDropBox = !(
    value?.start == null &&
    value?.end == null &&
    (value as DateTimeRangeWithRelative)?.relativeEndSecs == null &&
    (value as DateTimeRangeWithRelative)?.relativeStartSecs == null
  );
  const showClearButton =
    !disabled &&
    (!!dateValue.start ||
      (value as DateTimeRangeWithRelative)?.relativeEndSecs != null ||
      (value as DateTimeRangeWithRelative)?.relativeStartSecs != null);

  const shouldUseSingleDatePicker = isUnaryRangeFilterOp(operation);

  // It is implied that when shouldUseSingleDatePicker == true and useMinValueOnly == false, then useMaxValueOnly == true
  const useMinValueOnly = useMinFilterValue(operation);

  const getDisplayText = useCallback(() => {
    const suggestionText = tryGetDateRangePresetSuggestionText(
      value,
      useSiteTimeZone ? toZonedTime : undefined
    );

    if (suggestionText) {
      return suggestionText;
    }

    const suggestionTextRelative = tryGetDateRangeRelativePresetSuggestionText(
      value as DateTimeRangeWithRelative | null | undefined,
      useSiteTimeZone ? toZonedTime : undefined
    );

    if (suggestionTextRelative) {
      return suggestionTextRelative;
    }

    const { end, start } = dateValue;
    if (value && start) {
      if (useSiteTimeZone && value.start && value.end) {
        const startZoned = formatDate(
          toZonedTime(new Date(value.start)).toISOString(),
          currentLocale.code
        );
        const endZoned = formatDate(
          toZonedTime(new Date(value.end)).toISOString(),
          currentLocale.code
        );

        return `${startZoned} - ${endZoned}`;
      }
      return `${start} - ${end}`;
    }

    return defaultDateRangeDisplayText;
  }, [
    currentLocale.code,
    dateValue,
    defaultDateRangeDisplayText,
    toZonedTime,
    useSiteTimeZone,
    value,
  ]);

  const onDateRangePickerChange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      const newValue: DateTimeRangeWithRelative = value
        ? {
            relativeEndSecs: null,
            relativeStartSecs: null,
            roundingMode: null,
            ...value,
          }
        : {
            start: null,
            end: null,
            relativeStartSecs: null,
            relativeEndSecs: null,
            roundingMode: null,
          };

      newValue.start = from?.toISOString() || null;
      newValue.end = to?.toISOString() || null;
      newValue.relativeStartSecs = null;
      newValue.relativeEndSecs = null;

      setDateRange(newValue);
      setDateValue({
        start: formatDate(newValue.start || '', currentLocale.code),
        end: formatDate(newValue.end || '', currentLocale.code),
      });
    },
    [value, currentLocale.code]
  );

  const onClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDateRangePickerChange(undefined, undefined);
      onChange?.({ value: null, operation });
      onBlur?.({ value: null, operation });
    },
    [onBlur, onChange, onDateRangePickerChange, operation]
  );

  const onSuggestionClick = useCallback(
    (range: DateRange) => {
      onDateRangePickerChange(range.from, range.to);

      // Create the date range value to pass to onChange
      const newValue: DateTimeRangeWithRelative = {
        start: range.from?.toISOString() || null,
        end: range.to?.toISOString() || null,
        relativeStartSecs: null,
        relativeEndSecs: null,
        roundingMode: null,
      };

      // Immediately apply and close the modal
      onChange?.({ value: newValue, operation });
      onBlur?.({ value: newValue, operation });
      setIsOpen(false);
    },
    [onDateRangePickerChange, onChange, operation, onBlur]
  );

  const onSuggestionRelativeClick = useCallback(
    (range: DateTimeRangeWithRelative) => {
      setDateRange(range);
      setDateValue({
        start: formatDate('', currentLocale.code),
        end: formatDate('', currentLocale.code),
      });

      // Immediately apply and close the modal
      onChange?.({ value: range, operation });
      onBlur?.({ value: range, operation });
      setIsOpen(false);
    },
    [currentLocale.code, onChange, operation, onBlur]
  );

  const getFormattedDate = useCallback(
    (time: string | null | undefined) => {
      return useSiteTimeZone
        ? toZonedTime(time ? new Date(time) : new Date())
        : time
          ? new Date(time)
          : new Date();
    },
    [useSiteTimeZone, toZonedTime]
  );

  const DateRangeOptions = useMemo(() => {
    const selectorProps = {
      disabled,
      useSiteTimeZone,
      onSuggestionClick,
      dateValue,
      onSuggestionRelativeClick,
      dateRange,
      onPreviewChange,
      className,
      dateDisplayFormat,
      retainEndDateOnFirstSelection,
      isEndDateFixed,
      getFormattedDate,
      startDatePlaceholder,
      defaultFromlabel,
      endDatePlaceholder,
      defaultToLabel,
      rangeColors,
      locale,
      fromZonedTime,
      onDateRangePickerChange,
      trailingPresetNames,
      exactPresetNames,
      ...presetProps,
    };

    const SelectedDateSelector = dateSelectors[dateSelectorType];
    return SelectedDateSelector ? (
      <SelectedDateSelector {...selectorProps} />
    ) : (
      <div>Unsupported Date Selector</div>
    );
  }, [
    disabled,
    useSiteTimeZone,
    onSuggestionClick,
    dateValue,
    onSuggestionRelativeClick,
    dateRange,
    onPreviewChange,
    className,
    dateDisplayFormat,
    retainEndDateOnFirstSelection,
    isEndDateFixed,
    getFormattedDate,
    startDatePlaceholder,
    defaultFromlabel,
    endDatePlaceholder,
    defaultToLabel,
    rangeColors,
    locale,
    fromZonedTime,
    onDateRangePickerChange,
    trailingPresetNames,
    exactPresetNames,
    presetProps,
    dateSelectorType,
  ]);

  const displayTextTitleForDateRange = useMemo(() => {
    // If using a single date picker, don't show the display text
    if (shouldUseSingleDatePicker) {
      return undefined;
    }

    const displayText = getDisplayText();

    // only return if displayTex is string
    if (typeof displayText !== 'string') {
      return undefined;
    }

    return displayText;
  }, [getDisplayText, shouldUseSingleDatePicker]);

  return (
    <StackedFilter
      isHorizontalLayout={isHorizontalLayout}
      renderSingleDirectly
      FilterComponents={[
        ...(enableOperationSelect
          ? [
              <PosSelect
                key="operationFilter"
                value={operation ?? undefined}
                valueOptionsContent={DATE_RANGE_FILTER_OP_TO_CID}
                enableEmptySelection={false}
                onChange={(newOperation) => {
                  onChange?.({
                    value: dateRange,
                    operation: newOperation as FilterOperationType,
                  });
                  onBlur?.({
                    value: dateRange,
                    operation: newOperation as FilterOperationType,
                  });
                }}
              />,
            ]
          : []),
        shouldUseSingleDatePicker ? (
          <DatePickerInput
            value={
              useMinValueOnly
                ? dateValue.start
                  ? new Date(dateValue.start)
                  : null
                : dateValue.end
                  ? new Date(dateValue.end)
                  : null
            }
            onDateChange={(date: Date) => {
              if (useMinValueOnly) {
                onDateRangePickerChange(date, undefined);
                onChange?.({
                  value: {
                    start: date.toISOString(),
                    end: null,
                  } as DateTimeRangeWithRelative,
                  operation,
                });
                onBlur?.({
                  value: {
                    start: date.toISOString(),
                    end: null,
                  } as DateTimeRangeWithRelative,
                  operation,
                });
              } else {
                date = date
                  ? useSiteTimeZone
                    ? fromZonedTime(endOfDay(date))
                    : endOfDay(date)
                  : date;
                onDateRangePickerChange(undefined, date);
                onChange?.({
                  value: {
                    start: null,
                    end: date.toISOString(),
                  } as DateTimeRangeWithRelative,
                  operation,
                });
                onBlur?.({
                  value: {
                    start: null,
                    end: date.toISOString(),
                  } as DateTimeRangeWithRelative,
                  operation,
                });
              }
            }}
            locale={getLocaleFromLanguageOrCurrent()}
          />
        ) : (
          <Popover.Root
            key="dateRangeSelector"
            open={isOpen}
            onOpenChange={(isOpen: boolean) => {
              setIsOpen(isOpen);
            }}
          >
            <Popover.Trigger asChild>
              <button
                className={clsx(dateRangeStyles.dateRangeTrigger, {
                  [dateRangeStyles.dateRangeTriggerActive]: highlightDropBox,
                })}
                disabled={disabled}
              >
                <div
                  className={clsx(
                    dateRangeStyles.dateRangeTriggerContent,
                    styles.dateRangeTriggerContent
                  )}
                >
                  <div
                    className={styles.displayText}
                    style={displayTextStyle}
                    title={displayTextTitleForDateRange}
                  >
                    {getDisplayText()}
                  </div>
                  <div className={styles.dateRangeTriggerContentRight}>
                    {showClearButton && highlightDropBox && (
                      <div className={dateRangeStyles.iconContainer}>
                        <CrossIcon size={vars.iconSize.s} onClick={onClear} />
                      </div>
                    )}
                    <ExpandIcon size={vars.iconSize.s} />
                  </div>
                </div>
              </button>
            </Popover.Trigger>
            <Popover.Content
              align="start"
              className={dateRangeStyles.dateRangePopoverContent}
              forceMount
            >
              <div
                className={dateRangeStyles.dateRangePickerContainerV2}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className={styles.segmentedControlWrapper}>
                  <SegmentedControl
                    fullWidth
                    value={dateSelectorType}
                    withItemsBorders={false}
                    onChange={(value) => {
                      setDateSelectorType(value as DateRangeSelectorType);
                    }}
                    transitionDuration={500}
                    transitionTimingFunction="ease"
                    data={segmentedControlData}
                    classNames={{
                      label: styles.segmentedControlLabel,
                    }}
                    styles={{
                      root: {
                        backgroundColor: vars.color.surfaceSecondary,
                        borderRadius: vars.borderRadius.circle,
                        padding: 4,
                        border: `1px solid ${vars.color.borderPrimary}`,
                      },
                      indicator: {
                        backgroundColor: vars.color.surfaceBrand,
                        borderRadius: vars.borderRadius.circle,
                      },
                      label: {
                        fontSize: vars.typography.fontSize.base,
                        fontWeight: 400,
                      },
                    }}
                  />
                </div>
                {DateRangeOptions}
                {dateSelectorType === 'Custom' && (
                  <div className={styles.suggestionFooter}>
                    <Button
                      variant={'textPlain'}
                      style={{ paddingLeft: 0 }}
                      onClick={(e) => {
                        setIsOpen(false);
                      }}
                    >
                      <Content id={ContentId.Cancel} />
                    </Button>
                    <Button
                      variant={'regular'}
                      onClick={() => {
                        if (!dateRange) {
                          return;
                        }
                        onChange?.({ value: dateRange, operation });
                        onBlur?.({ value: dateRange, operation });
                        setIsOpen(false);
                      }}
                    >
                      <Content id={ContentId.Save} />
                    </Button>
                  </div>
                )}
              </div>
            </Popover.Content>
          </Popover.Root>
        ),
      ]}
    />
  );
};
